import { FieldPolicy, Reference } from "@apollo/client";
import { mergeWith } from "lodash";

type KeyArgs = FieldPolicy["keyArgs"];

type PaginatedResult<T = Reference> = {
  nodes: T[];
  // Rails API uses `metadata` for pagination
  metadata?: {
    currentPage: number;
  };
  // Go API uses `meta` for pagination
  meta?: {
    pageIndex: number;
  };
};

const mergeArrays = (objValue: unknown, srcValue: unknown) => {
  return Array.isArray(objValue) ? objValue.concat(srcValue) : undefined;
};

// TODO: it would be **much** better if we could use one of apollo's built-in policies (preferably `relayStylePagination`)
//  but this would require a **major** overhaul on the back-end
export const paginationFieldPolicy = <T = Reference>(
  keyArgs: KeyArgs = false,
): FieldPolicy<PaginatedResult<T>> => ({
  keyArgs,
  merge(existing, incoming) {
    const existingPage = existing?.metadata?.currentPage || existing?.meta?.pageIndex || 0;
    const incomingPage = incoming?.metadata?.currentPage || incoming?.meta?.pageIndex || 0;

    if (existingPage >= incomingPage) {
      return incoming;
    }

    return mergeWith({}, existing, incoming, mergeArrays);
  },
});

export const unshiftFieldPolicy = (keyArgs?: KeyArgs): FieldPolicy => ({
  keyArgs,

  merge(existing = [], incoming) {
    return [...incoming, ...existing];
  },
});

// `Query.orders` is shared by documents with different selections, so the key args are every
// argument that changes *which* rows come back, plus `limit`. `page` stays out so pages of the
// same list accumulate.
//
// `state` filters rows: the homepage's `HomepageActiveConceptOrders` pins
// `state: concept_workflow` and has no `q`, while the New Project dialog's `Projects` document
// declares `q` but leaves it undefined until the user searches. Apollo omits absent arguments
// from the field key, so without `state` both collapse to the same entry and the dialog reads
// the homepage's concept-workflow projects.
//
// `limit` selects nothing but is still identity: the merge above compares only page numbers, so
// an entry is only coherent while every page in it is the same size. Documents hold their
// `limit` constant (12 in the dialog and homepage, 20 in the Project Tracker; `useLoadMore`
// varies only `page`), so keying on it never splits a live list — it fences differently sized
// windows into separate entries.
//
// `organizationalUnits` and `conceptWorkflow` filter rows for the Project Tracker's
// SearchProjects document; without them switching filters briefly renders rows cached for the
// previous filter.
export const ordersFieldPolicy = paginationFieldPolicy([
  "q",
  "sort",
  "state",
  "limit",
  "organizationalUnits",
  "conceptWorkflow",
]);

type RevisionCommentsThread = {
  convergence?: unknown;
} & Record<string, unknown>;

// `Revision.comments` — the governed thread read (the read-your-writes
// fence pilot's presence surface). Two rules, both load-bearing:
//
// `keyArgs: false`: the read's `atLeast` fence argument is a per-request
// causal anchor, not an identity — every variant answers about the SAME
// thread, and the normalized `Revision.comments` field is the one seam
// every comment-carrying document converges through. Keying by the
// argument would fork that seam per token and strand watchers on a
// variant nobody writes again.
//
// The merge strips `convergence`: the wrapper's disclosure is
// REQUEST-scoped transport metadata — whether THIS read converged on the
// caller's fence token — never thread state. On the shared seam a cached
// copy could only ever alias some other read's claim, so it is never
// stored; cache reads of `convergence` are answered by the companion
// read policy below with the constant no-claim meta instead. The merge
// SPREADS over the existing wrapper: a write whose selection lacks
// `items` (a convergence-only probe document) must never replace the
// cached thread with an items-less object.
export const revisionCommentsFieldPolicy: FieldPolicy<RevisionCommentsThread> = {
  keyArgs: false,
  merge(existing, incoming) {
    if (!incoming) {
      return incoming;
    }
    const { convergence: _requestScoped, ...thread } = incoming;
    return existing ? { ...existing, ...thread } : thread;
  },
};

// The honest constant for a cache-served `convergence`: a cache answer is
// not a fenced answer, and `converged` is a POSITIVE claim only — claiming
// nothing is always sound.
const noClaimConvergence = Object.freeze({
  __typename: "ConvergenceMeta" as const,
  fenced: false,
  converged: false,
  waitedMs: null,
  uncovered: null,
});

// The ONE companion read policy for every never-stored convergence field
// (`RevisionComments.convergence` and `CommentOperationCheck.convergence`
// — registered under both types in client.tsx): the merges never store a
// disclosure, so without a read policy every document selecting
// `convergence` would diff permanently incomplete (cache-first
// degenerating to network-only, readQuery returning null). The read
// synthesizes the no-claim constant instead: complete diffs, and the one
// claim a shared cache can honestly make. Single-homed on purpose — the
// synthesis rule is the aliasing defense, and two copies would drift.
// The consequence to design around: a fenced probe that must SEE its own
// live disclosure reads it off the response boundary (the Apollo link
// layer — fence-capture-link), never through the normalized cache —
// which also keeps transient probe variants from churning the shared
// seam.
export const noClaimConvergenceReadFieldPolicy: FieldPolicy<typeof noClaimConvergence> = {
  read(existing) {
    return existing ?? noClaimConvergence;
  },
};

type CommentOperationCheckEntry = {
  convergence?: unknown;
} & Record<string, unknown>;

// `Comment.viewerOperationChecks` — the vOC twin of the thread policy
// above, and a hard prerequisite for any document selecting a payload's
// per-check `convergence` (the mutation self-fenced signal): the check
// objects normalize INLINE under `Comment:<id>`, so a mutation-time
// `converged: true` stored there would be served to every later reader of
// the same field as a stale convergence claim — the exact aliasing class
// the thread merge exists to prevent. The merge strips each check's
// request-scoped `convergence` before storage (the record legs —
// operation/authorization/validation — store as served), and the shared
// no-claim read policy answers cache reads.
//
// `keyArgs: ["operations"]`: which operations were asked IS identity
// (different lists are different answers), but the fence's `atLeast`
// argument is a per-request causal anchor, not identity — the same seam
// rule as the thread read's `keyArgs: false`.
export const commentViewerOperationChecksFieldPolicy: FieldPolicy<
  CommentOperationCheckEntry[] | null
> = {
  keyArgs: ["operations"],
  merge(_existing, incoming, { isReference }) {
    if (!Array.isArray(incoming)) {
      return incoming;
    }
    // The common case — every list document's write — carries no
    // disclosure to strip: return the incoming array untouched instead
    // of rebuilding byte-identical copies.
    if (!incoming.some((check) => check && typeof check === "object" && "convergence" in check)) {
      return incoming;
    }
    return incoming.map((check) => {
      if (!check || typeof check !== "object" || isReference(check)) {
        return check;
      }
      const { convergence: _requestScoped, ...record } = check;
      return record;
    });
  },
};
